import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhoneOff, Loader2 } from 'lucide-react';
import TimerStrip from './components/TimerStrip';
import VideoArea from './components/VideoArea';
import PatientPanel from './components/PatientPanel';
import { useVisitStore } from '@/stores/visitStore';
import { useSessionStore } from '@/stores/sessionStore';
import { useUpdateEncounter } from '@/fhir/hooks';
import { FhirErrorBanner } from '@/fhir/errors';
import { useTelehealthMetering } from './useTelehealthMetering';
import { getViewConfig } from '@/auth/viewConfig';
import { useAuth } from '@/auth/AuthProvider';

/**
 * LiveVisit Component — TV-P1-04 / TV-RV-09
 *
 * Main visit screen:
 * - Top: Timer strip + End Visit button (TV-P1-04).
 * - Left ~70%: Video area.
 * - Right ~30%: Tabbed patient panel (provider only; hidden for front-desk monitor).
 *
 * TV-RV-09 monitor mode (front-desk):
 *   - visitMode='monitor' → renders the video room but hides the clinical PatientPanel.
 *   - A "Monitoring" banner replaces the right-side panel so there's no dead whitespace.
 *   - The End Visit / confirm-end flow is suppressed; front-desk leaves silently via
 *     the browser back button or the Leave Visit button (no FHIR Encounter update).
 *   - useTelehealthMetering is called but internally no-ops for non-clinical visitMode
 *     (TV-RV-09 guard in useTelehealthMetering.js).
 *
 * The encounterId in the URL is the FHIR Encounter UUID created by
 * ProviderQueue.handleStartVisit (TV-P1-03). End Visit PUTs that
 * Encounter to status=finished and navigates back to the queue.
 */
export default function LiveVisit() {
  const navigate = useNavigate();
  const { encounterId } = useParams();
  const { user } = useAuth();
  const cfg = getViewConfig(user?.role ?? 'patient');
  const isMonitor = cfg.visitMode === 'monitor';

  const { startVisit, endVisit, visitStatus } = useVisitStore();
  const visitEncounterId = useVisitStore((s) => s.encounterId);
  const recordCallEnd = useTelehealthMetering();

  // Patient context comes from the SMART launch — AuthProvider stuffs
  // it into the session store via setCurrentPatient(smartPatientId).
  const patientId = useSessionStore(
    (s) => s.currentPatient || s.smartContext?.patientId || null,
  );
  const setCurrentEncounter = useSessionStore((s) => s.setCurrentEncounter);
  const updateEncounter = useUpdateEncounter();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [endError, setEndError] = useState(null);

  // Initialize visit timer on mount.
  // TV-RV-09: monitor-mode joins must NOT start the clinical timer —
  // only the provider (visitMode='clinical') drives startVisit/endVisit.
  useEffect(() => {
    if (isMonitor) {
      // Front-desk observer: track the encounter in session so the
      // AppShell phase ribbon can highlight the current phase, but
      // do not touch the visit store timer.
      if (encounterId) setCurrentEncounter(encounterId);
      return;
    }
    // R3-2 — also restart when the persisted store belongs to a
    // DIFFERENT encounter. visit-store survives in localStorage, so a
    // stale 'in-progress' from a previous session used to suppress
    // startVisit() here and the timer ran off the old startTime.
    if (
      encounterId &&
      (visitStatus !== 'in-progress' || visitEncounterId !== encounterId)
    ) {
      startVisit(null, encounterId);
    }
    if (encounterId) {
      setCurrentEncounter(encounterId);
    }
  }, [encounterId, visitStatus, visitEncounterId, startVisit, setCurrentEncounter, isMonitor]);

  // Handle browser back/close - warn user
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleEndVisit = async () => {
    setEndError(null);
    if (!encounterId) {
      // Synthesize a 400-ish error so FhirErrorBanner renders consistent copy.
      setEndError({
        status: 400,
        message: 'No encounter id on the route — cannot finalize the visit on the FHIR side.',
      });
      return;
    }
    try {
      await updateEncounter.mutateAsync({ encounterId });

      // Finalize the local timer FIRST — endVisit() runs
      // calculateVisitDuration and freezes faceToFaceSeconds in the
      // store. The previous order POSTed metering BEFORE endVisit(),
      // reading the live store value — which is only set by
      // updateTimingMetrics (never scheduled during a visit), i.e.
      // always 0 → useTelehealthMetering threw "durationSeconds must
      // be > 0" and NO usage event was ever recorded.
      endVisit();
      const finalF2F = useVisitStore.getState().faceToFaceSeconds;

      // TV-P4-01 — record the telehealth call against the provider
      // tenant's metered usage. Fire AFTER the Encounter PUT so we
      // never bill for a visit that didn't finalize. NON-CRITICAL:
      // failures here MUST NOT block the navigate-back-to-queue; we
      // log + surface a soft warning instead, since the visit is
      // already complete from the patient's POV. Idempotent on
      // callId, so a manual retry path remains open.
      try {
        await recordCallEnd.mutateAsync({
          callId: encounterId,
          durationSeconds: finalF2F,
          patientId: patientId || undefined,
        });
      } catch (mErr) {
        console.error('[LiveVisit] telehealth metering POST failed:', mErr);
      }

      setCurrentEncounter(null);
      navigate('/queue');
    } catch (err) {
      console.error('[LiveVisit] End Visit failed:', err);
      setEndError(err);
    }
  };

  return (
    <div className="h-screen bg-slate-950 flex flex-col overflow-hidden">
      {/* Timer Strip + End Visit / Leave Visit control */}
      <div className="flex items-stretch border-b border-slate-700">
        <div className="flex-1">
          <TimerStrip />
        </div>
        <div className="bg-slate-900 border-l border-slate-700 px-4 flex items-center gap-3">
          {/* TV-RV-09: front-desk monitor sees a non-destructive "Leave" button;
              no FHIR Encounter update, no billing event. */}
          {isMonitor ? (
            <button
              onClick={() => navigate('/queue')}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium text-sm rounded-lg transition-colors"
            >
              <PhoneOff className="w-4 h-4" />
              Leave Visit
            </button>
          ) : (
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={updateEncounter.isPending || !encounterId}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm rounded-lg transition-colors"
            >
              {updateEncounter.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <PhoneOff className="w-4 h-4" />
              )}
              {updateEncounter.isPending ? 'Ending…' : 'End Visit'}
            </button>
          )}
        </div>
      </div>

      {/* Inline error banner if the End Visit PUT failed */}
      {endError && (
        <div className="px-6 py-2">
          <FhirErrorBanner error={endError} onDismiss={() => setEndError(null)} />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex gap-4 p-4 overflow-hidden">
        {/* Video Area — full width for monitor, ~70% for provider */}
        <div className={isMonitor ? 'flex-1' : 'flex-1'}>
          <VideoArea encounterId={encounterId} />
        </div>

        {/* TV-RV-09: clinical PatientPanel hidden for front-desk monitor.
            A minimal status banner replaces it so the layout isn't just blank. */}
        {isMonitor ? (
          <div className="w-[28%] flex flex-col gap-3">
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider">
                  Monitor Mode
                </p>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                You are observing this visit as front-desk staff. Clinical
                notes and chart data are not available in this view.
              </p>
              {patientId && (
                <p className="text-xs text-slate-500 mt-3">
                  Patient: <span className="font-mono text-slate-300">{patientId}</span>
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="w-[30%]">
            <PatientPanel patientId={patientId} />
          </div>
        )}
      </div>

      {/* Confirm-end modal — provider only (not shown in monitor mode) */}
      {!isMonitor && confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-2">End this visit?</h3>
            <p className="text-sm text-slate-400 mb-5">
              The Encounter will be marked as finished in the FHIR server and the
              video session will disconnect. Any unsaved notes should be added
              before continuing.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={updateEncounter.isPending}
                className="px-4 py-2 text-sm text-slate-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setConfirmOpen(false);
                  await handleEndVisit();
                }}
                disabled={updateEncounter.isPending}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg"
              >
                {updateEncounter.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                End Visit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
